import { Link, useLocation } from 'react-router-dom';
import { ShieldOff, ArrowRight, ArrowLeft } from 'lucide-react';
import { Logo } from '@/components/common/Logo';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { Badge } from '@/components/ui/Badge';
import { useAuth, isAdmin, isDoctor } from '@/providers/AuthProvider';

interface Props {
  /** Where the user tried to go — surfaced so they understand what was blocked. */
  attemptedPath?: string;
  requiredRole?: 'doctor' | 'admin';
}

export function UnauthorizedPage({ attemptedPath, requiredRole }: Props) {
  const { user } = useAuth();
  const location = useLocation();
  const path = attemptedPath ?? location.pathname;

  // Suggest the most useful destination for the current role
  const homeFor =
    isAdmin(user?.role) ? '/admin/dashboard'
    : isDoctor(user?.role) ? '/portal/dashboard'
    : '/';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
      <Logo size="lg" />
      <div className="mt-12 flex h-16 w-16 items-center justify-center rounded-2xl bg-danger/10 text-danger">
        <ShieldOff className="h-8 w-8" />
      </div>
      <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-danger">403 — Access denied</p>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-ink">
        You don't have access to this page
      </h1>
      <p className="mt-4 max-w-lg text-ink-muted">
        {requiredRole === 'admin' ? (
          <>This area is restricted to Locum Network staff. Your account is signed in as a <strong className="text-ink">doctor</strong>, which doesn't include admin access.</>
        ) : requiredRole === 'doctor' ? (
          <>This area is for registered doctors. Your account is signed in as <strong className="text-ink">{user?.role.replace('_', ' ')}</strong>.</>
        ) : (
          <>You need to be signed in with the right role to view this page.</>
        )}
      </p>

      <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-border bg-bg px-3 py-1 text-xs text-ink-muted">
        <Badge tone="neutral">Tried to open</Badge>
        <code className="font-mono text-ink">{path}</code>
      </div>

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <ButtonLink to={homeFor} iconLeft={<ArrowLeft className="h-4 w-4" />}>
          Back to {homeFor === '/' ? 'home' : 'your dashboard'}
        </ButtonLink>
        {!user && (
          <ButtonLink to="/login" variant="outline" iconRight={<ArrowRight className="h-4 w-4" />}>
            Sign in
          </ButtonLink>
        )}
      </div>

      <p className="mt-10 text-xs text-ink-subtle">
        Think this is a mistake? <Link to="/contact" className="link">Contact support</Link>.
      </p>
    </div>
  );
}
